import { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';

/** Small, non-modal popover: escapes scrolling sidebars and returns keyboard focus. */
export default function WorkbenchPopover({ label, trigger, children, className = '', width = 280 }) {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchor = useRef(null);
  const panel = useRef(null);
  const id = useId();
  function close(restore = false) {
    setOpen(false);
    if (restore) anchor.current?.focus();
  }
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = anchor.current.getBoundingClientRect();
      const size = Math.min(width, window.innerWidth - 24);
      const height = panel.current?.offsetHeight || 240;
      setPosition({ width: size, left: Math.max(12, Math.min(rect.left, window.innerWidth - size - 12)), top: Math.max(12, rect.bottom + height + 12 < window.innerHeight ? rect.bottom + 6 : rect.top - height - 6) });
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(panel.current);
    const outside = (event) => { if (!panel.current?.contains(event.target) && !anchor.current?.contains(event.target)) close(); };
    const key = (event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); } };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', key, true);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    panel.current?.querySelector('button, select, input, [tabindex="0"]')?.focus();
    return () => { observer.disconnect(); document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', outside); document.removeEventListener('keydown', key, true); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, width]);
  return <>
    <button ref={anchor} type="button" className={className} aria-label={label} title={label} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(!open)}>{trigger}</button>
    {open && createPortal(<motion.div initial={reduce ? false : { opacity: 0, y: 6, scale: .98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: reduce ? 0 : .16, ease: [.2, .8, .2, 1] }} ref={panel} id={id} role="dialog" aria-label={label} className="workbench-popover" style={{ ...position, visibility: position ? 'visible' : 'hidden' }}>{typeof children === 'function' ? children(() => close(true)) : children}</motion.div>, document.body)}
  </>;
}
